import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Users, MapPin, Calendar, Truck, FileText, DollarSign, 
  BarChart3, Smartphone, ArrowRight, Check, Play,
  Clock, Route, Shield, Zap, CreditCard, Banknote, Cog,
  Bot, Sparkles, MessageSquare
} from 'lucide-react';

const LandingPage = () => {
  const features = [
    {
      icon: Bot,
      title: "AI Assistant",
      description: "Ask questions, get insights, and manage your business with natural language commands powered by AI.",
      link: "/features/ai-assistant",
      highlight: true
    },
    {
      icon: MapPin,
      title: "Property Management",
      description: "Store detailed property information including service instructions, access codes, and preferred schedules.",
      link: "/features/property-management"
    },
    {
      icon: Calendar,
      title: "Scheduling & Routes",
      description: "Automate recurring services and optimize routes to reduce drive time and fuel costs.",
      link: "/features/scheduling"
    },
    {
      icon: Users,
      title: "Crew Management",
      description: "Assign crews to routes, track progress in real-time, and manage your entire workforce.",
      link: "/features/crew-management"
    },
    {
      icon: FileText,
      title: "Estimates & Proposals",
      description: "Create professional estimates, send them instantly, and convert approvals into scheduled jobs.",
      link: "/features/estimates-invoicing"
    },
    {
      icon: DollarSign,
      title: "Invoicing & Payments",
      description: "Generate invoices automatically, accept payments online, and track outstanding balances.",
      link: "/features/estimates-invoicing"
    },
    {
      icon: Smartphone,
      title: "Mobile Crew App",
      description: "Simple mobile interface for field crews to view routes, complete jobs, and upload photos.",
      link: "/features/mobile"
    },
    {
      icon: BarChart3,
      title: "Reports & Analytics",
      description: "Track revenue, job completion rates, crew productivity, and business growth metrics.",
      link: "/features/reporting"
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: "Save 10+ Hours Per Week",
      description: "Eliminate manual scheduling, routing, and paperwork with automated workflows."
    },
    {
      icon: Zap,
      title: "Increase Efficiency by 30%",
      description: "Optimized routes mean more jobs completed with less drive time and fuel costs."
    },
    {
      icon: Shield,
      title: "Never Miss a Service",
      description: "Automated recurring schedules ensure every property is serviced on time, every time."
    }
  ];

  return (
    <MarketingLayout>
      {/* Hero Section */}
      <section className="pt-16 pb-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                AI Powered Platform
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
                The Operating System for{' '}
                <span className="text-primary">Landscaping Companies</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                Manage your business your way. Use our full featured platform manually, or let the 
                AI assistant handle tasks for you. Schedule crews, create estimates, and get answers 
                with simple commands.
                <span className="font-medium text-violet-600"> You always approve before it acts.</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button data-testid="hero-cta-btn" size="lg" className="bg-primary hover:bg-primary/90 text-lg px-8 h-14">
                    Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/features">
                  <Button variant="outline" size="lg" className="text-lg px-8 h-14">
                    <Play className="mr-2 h-5 w-5" /> See How It Works
                  </Button>
                </Link>
              </div>
              <p className="mt-6 text-sm text-gray-500">
                No credit card required • Free for payment processing customers
              </p>
            </div>
            <div className="relative animate-fade-in">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
                <img 
                  src="https://images.unsplash.com/photo-1734303023481-7508b5c9f1ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBsYW5kc2NhcGVyJTIwbW93aW5nJTIwYmVhdXRpZnVsJTIwbGF3biUyMHJpZGluZyUyMG1vd2VyfGVufDB8fHx8MTc3MzgzNjk0NXww&ixlib=rb-4.1.0&q=85&w=1200" 
                  alt="Professional landscaper on riding mower maintaining lawn" 
                  className="w-full h-auto min-h-[450px] object-cover"
                />
              </div>
              {/* Floating software preview */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-500 rounded-lg flex items-center justify-center">
                    <Bot className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">AI Assistant</p>
                    <p className="text-sm text-gray-500">"Assign crew to job"</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Works for You</p>
                    <p className="text-sm text-gray-500">24/7 availability</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-4">
              Everything You Need to Run Your Business
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Built specifically for landscaping workflows—not generic field service software.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Link to={feature.link} key={index}>
                <Card 
                  className={`hover:shadow-lg transition-all duration-200 h-full group ${
                    feature.highlight 
                      ? 'bg-gradient-to-br from-violet-600 to-indigo-700 border-violet-500 hover:shadow-violet-200' 
                      : 'bg-white hover:border-primary/20 border-gray-100'
                  }`}
                  data-testid={`feature-card-${index}`}
                >
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors ${
                      feature.highlight 
                        ? 'bg-white/20' 
                        : 'bg-primary/10 group-hover:bg-primary/20'
                    }`}>
                      <feature.icon className={`h-6 w-6 ${feature.highlight ? 'text-white' : 'text-primary'}`} />
                    </div>
                    {feature.highlight && (
                      <div className="inline-flex items-center gap-1 bg-white/20 text-white text-xs px-2 py-1 rounded-full mb-2">
                        <Sparkles className="h-3 w-3" /> AI-Powered
                      </div>
                    )}
                    <h3 className={`text-lg font-semibold mb-2 ${feature.highlight ? 'text-white' : 'text-gray-900'}`}>
                      {feature.title}
                    </h3>
                    <p className={`text-sm leading-relaxed mb-4 ${feature.highlight ? 'text-violet-100' : 'text-gray-600'}`}>
                      {feature.description}
                    </p>
                    <span className={`text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all ${
                      feature.highlight ? 'text-white' : 'text-primary'
                    }`}>
                      Learn more <ArrowRight className="h-4 w-4" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/features">
              <Button variant="outline" size="lg">
                View All Features <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-4">
              Up and Running in Minutes
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Getting started with Orchard LMS is simple. No complex setup or lengthy onboarding.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Sign Up Free", description: "Create your account in under 2 minutes. No credit card required." },
              { step: "2", title: "Add Properties", description: "Import your customer list or add properties one by one with all the details." },
              { step: "3", title: "Set Up Schedules", description: "Create recurring service schedules and build optimized routes." },
              { step: "4", title: "Start Working", description: "Your crews see their routes on mobile and you track everything in real-time." }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="w-14 h-14 bg-primary text-white rounded-2xl flex items-center justify-center text-xl font-bold mb-4">
                  {item.step}
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-7 left-16 w-[calc(100%-4rem)] h-0.5 bg-gray-200"></div>
                )}
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Assistant Showcase Section */}
      <section className="py-24 bg-gradient-to-br from-violet-950 via-indigo-950 to-slate-900 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-violet-500/20 text-violet-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                Powered by AI
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                Your AI-Powered Business Assistant
              </h2>
              <p className="text-lg text-gray-300 mb-8">
                Just ask. Our AI assistant understands your business and can answer questions, provide insights, and even perform actions—all through simple conversation.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-violet-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="h-4 w-4 text-violet-300" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Ask Anything</h4>
                    <p className="text-gray-400 text-sm">"How many jobs are scheduled today?" "What's our revenue this month?"</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-violet-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="h-4 w-4 text-violet-300" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Take Action</h4>
                    <p className="text-gray-400 text-sm">"Assign Team Alpha to the Johnson job" "Create an estimate for $500"</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-violet-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Bot className="h-4 w-4 text-violet-300" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Get Recommendations</h4>
                    <p className="text-gray-400 text-sm">"Which crew should handle this job?" "What customers need follow-up?"</p>
                  </div>
                </div>
              </div>

              <Link to="/pricing">
                <Button size="lg" className="bg-violet-600 hover:bg-violet-700 text-white">
                  Try AI Assistant Free <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            {/* Chat Demo */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 to-indigo-600/20 rounded-3xl blur-3xl"></div>
              <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden max-w-md mx-auto">
                {/* Chat Header */}
                <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-4 py-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Bot className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm">AI Assistant</h3>
                    <p className="text-emerald-100 text-xs">Always available</p>
                  </div>
                </div>
                
                {/* Chat Messages */}
                <div className="p-4 space-y-4 bg-gray-50 min-h-[300px]">
                  <div className="flex justify-end">
                    <div className="bg-emerald-600 text-white px-4 py-2 rounded-2xl rounded-br-md text-sm max-w-[80%]">
                      Which crew should I assign to today's jobs?
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-white shadow-sm border border-gray-100 px-4 py-2 rounded-2xl rounded-bl-md text-sm max-w-[80%] text-gray-800">
                      <div className="flex items-start gap-2">
                        <Bot className="h-4 w-4 text-emerald-600 mt-0.5" />
                        <div>
                          <p>You have 4 jobs scheduled today. Based on location and workload, I recommend:</p>
                          <ul className="mt-2 space-y-1 text-xs">
                            <li>• <strong>Team Alpha</strong> → Wilson & Johnson properties (north area)</li>
                            <li>• <strong>Team Bravo</strong> → Smith & Davis properties (south area)</li>
                          </ul>
                          <p className="mt-2 text-xs text-gray-500">Want me to assign these crews?</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="bg-emerald-600 text-white px-4 py-2 rounded-2xl rounded-br-md text-sm max-w-[80%]">
                      Yes, assign them
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-white shadow-sm border border-gray-100 px-4 py-2 rounded-2xl rounded-bl-md text-sm max-w-[80%] text-gray-800">
                      <div className="flex items-start gap-2">
                        <Bot className="h-4 w-4 text-emerald-600 mt-0.5" />
                        <div>
                          <p>✅ Done! I've assigned:</p>
                          <ul className="mt-1 space-y-0.5 text-xs">
                            <li>• Team Alpha → 2 jobs</li>
                            <li>• Team Bravo → 2 jobs</li>
                          </ul>
                          <p className="mt-1 text-xs text-emerald-600">All crews have been notified.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Chat Input */}
                <div className="p-3 border-t border-gray-100 bg-white">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Ask about your business..."
                      className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-full bg-gray-50 text-gray-400"
                      disabled
                    />
                    <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
                      <ArrowRight className="h-4 w-4 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Complete Ecosystem Section */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Zap className="h-4 w-4" />
              Everything Your Business Needs
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Software + Payments + Financing
            </h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              Orchard LMS isn't just software—it's a complete ecosystem designed to help landscaping companies manage operations, process payments, and access the capital needed to grow.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700 hover:border-emerald-500/50 transition-colors">
              <div className="w-14 h-14 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-6">
                <Cog className="h-7 w-7 text-emerald-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Operations Software</h3>
              <p className="text-gray-400 mb-4">
                Manage your entire operation—customers, properties, crews, scheduling, routes, and invoicing—all in one platform built specifically for landscaping.
              </p>
              <Link to="/features" className="text-emerald-400 font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">
                Explore Features <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700 hover:border-blue-500/50 transition-colors">
              <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center mb-6">
                <CreditCard className="h-7 w-7 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Payment Processing</h3>
              <p className="text-gray-400 mb-4">
                Accept credit cards, debit cards, and ACH payments. Get paid faster with online payments and automatic invoicing. Use our processing and the software is free.
              </p>
              <Link to="/pricing" className="text-blue-400 font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">
                View Pricing <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700 hover:border-amber-500/50 transition-colors">
              <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center mb-6">
                <Banknote className="h-7 w-7 text-amber-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Business Financing</h3>
              <p className="text-gray-400 mb-4">
                Access working capital, term loans, and equipment financing. Your business data helps you qualify for the funding you need to hire crews, buy equipment, and grow.
              </p>
              <Link to="/financing" className="text-amber-400 font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">
                Explore Financing <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-500/10 via-blue-500/10 to-amber-500/10 rounded-2xl p-8 border border-gray-700">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">One Platform. Complete Solution.</h3>
                <p className="text-gray-400 mb-6">
                  Stop juggling multiple tools and vendors. With Orchard LMS, your software generates the data, your payments build your track record, and your financing options grow with your business.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-gray-300">All credit types welcome for financing</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-gray-300">Equipment, working capital, and term loans</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-gray-300">Funding decisions based on real business data</span>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <Link to="/apply">
                  <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 text-lg px-8 h-14">
                    Apply for Financing <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <p className="text-sm text-gray-500 mt-4">Quick application • Decisions in 24-48 hours</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-24 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Choose the plan that works for you. Free with integrated payment processing, 
            or a flat monthly fee to use your own.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button 
                size="lg" 
                className="bg-white text-primary hover:bg-gray-100 text-lg px-8 h-14"
                data-testid="cta-signup-btn"
              >
                Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button 
                size="lg" 
                variant="outline"
                className="border-white text-white hover:bg-white/10 text-lg px-8 h-14"
              >
                View Pricing Details
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to Transform Your Landscaping Business?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Join landscaping companies who are saving time, reducing costs, and growing their businesses with Orchard LMS.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-lg px-8 h-14">
              Start Your Free Account <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default LandingPage;
